import { BOLD_SCHEMA } from '@adornis/prosemirror-editor/marks/bold/schema.js';
import { ITALIC_SCHEMA } from '@adornis/prosemirror-editor/marks/italic/schema.js';
import { PARAGRAPH_SCHEMA } from '@adornis/prosemirror-editor/nodes/paragraph/schema.js';
import { HARD_BREAK_SCHEMA } from '@adornis/prosemirror-editor/nodes/hard-break/schema.js';
import { Schema, type Node as ProseMirrorNode } from 'prosemirror-model';
import type { WebsiteBuilderNodeJSON } from './types.js';

export const COMMENT_TEXT_LIMIT = 4000;
export const safeCommentHref = (value: unknown): string | null => {
  if (typeof value !== 'string' || /[\u0000-\u0020]/u.test(value)) return null;
  if (/^(https?:\/\/|mailto:)/iu.test(value)) return value;
  if (/^\/(?!\/)/u.test(value) || value.startsWith('#')) return value;
  return null;
};

/** Deliberately small: no images, embeds, arbitrary HTML or application entities. */
export const commentSchema = new Schema({
  nodes: {
    doc: { content: 'block+' },
    paragraph: PARAGRAPH_SCHEMA(),
    text: { group: 'inline' },
    hard_break: HARD_BREAK_SCHEMA(),
    bullet_list: { group: 'block', content: 'list_item+', toDOM: () => ['ul', 0], parseDOM: [{ tag: 'ul' }] },
    ordered_list: { group: 'block', content: 'list_item+', toDOM: () => ['ol', 0], parseDOM: [{ tag: 'ol' }] },
    list_item: { content: 'paragraph block*', toDOM: () => ['li', 0], parseDOM: [{ tag: 'li' }] },
  },
  marks: {
    bold: BOLD_SCHEMA(),
    italic: ITALIC_SCHEMA(),
    link: {
      attrs: { href: {} }, inclusive: false,
      toDOM: mark => ['a', { href: safeCommentHref(mark.attrs.href) ?? '#', rel: 'noopener noreferrer' }, 0],
      parseDOM: [{ tag: 'a[href]', getAttrs: element => {
        const href = safeCommentHref((element as HTMLElement).getAttribute('href'));
        return href ? { href } : false;
      } }],
    },
  },
});

export const commentBodyFromText = (text: string): WebsiteBuilderNodeJSON => ({
  type: 'doc',
  content: text.replace(/\r\n?/gu, '\n').split('\n').map(line => ({
    type: 'paragraph', ...(line ? { content: [{ type: 'text', text: line }] } : {}),
  })),
});

export const commentPlainText = (doc: ProseMirrorNode): string => doc.textBetween(0, doc.content.size, '\n', '\n');

export const normalizeCommentBody = (value: unknown, allowEmpty = false): WebsiteBuilderNodeJSON | null => {
  try {
    if (!value || typeof value !== 'object' || JSON.stringify(value).length > 64000) return null;
    const doc = commentSchema.nodeFromJSON(value);
    if (doc.type !== commentSchema.topNodeType) return null;
    doc.check();
    let valid = true;
    doc.descendants(node => {
      for (const mark of node.marks) if (mark.type.name === 'link' && !safeCommentHref(mark.attrs.href)) valid = false;
    });
    const text = commentPlainText(doc);
    return valid && text.length <= COMMENT_TEXT_LIMIT && (allowEmpty || text.trim().length > 0)
      ? doc.toJSON() as WebsiteBuilderNodeJSON : null;
  } catch { return null; }
};

export interface CommentAnchor {
  pageKey: string;
  audience: string;
  elementId: string;
  x: number;
  y: number;
  range?: { from: number; to: number; locale: string };
  documentRevision?: string;
}

export interface WebEditorComment {
  id: string;
  text: string;
  body?: WebsiteBuilderNodeJSON;
  status: 'open' | 'resolved';
  anchor: CommentAnchor;
  createdLocale: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  revision: number;
  resolvedBy?: string;
  resolvedAt?: string;
}

export interface WebEditorCommentsAdapter {
  list(projectId: string): Promise<readonly WebEditorComment[]>;
  create(projectId: string, input: Pick<WebEditorComment, 'body' | 'anchor' | 'createdLocale'>): Promise<WebEditorComment>;
  update(projectId: string, id: string, expectedRevision: number, update: Partial<Pick<WebEditorComment, 'body' | 'anchor' | 'status'>>): Promise<WebEditorComment>;
  delete(projectId: string, id: string, expectedRevision: number): Promise<void>;
  subscribe(projectId: string, onChange: () => void): () => void;
}

export const normalizeCommentAnchor = (value: unknown): CommentAnchor | null => {
  if (!value || typeof value !== 'object') return null;
  const input = value as Partial<CommentAnchor>;
  if (![input.pageKey, input.audience, input.elementId].every(part => typeof part === 'string' && part.length > 0 && part.length <= 240)) return null;
  if (typeof input.x !== 'number' || !Number.isFinite(input.x) || typeof input.y !== 'number' || !Number.isFinite(input.y)) return null;
  if (input.range && (!Number.isInteger(input.range.from) || !Number.isInteger(input.range.to) || input.range.from < 0 || input.range.to < input.range.from || typeof input.range.locale !== 'string')) return null;
  if (input.documentRevision !== undefined && (typeof input.documentRevision !== 'string' || input.documentRevision.length > 100)) return null;
  return { pageKey: input.pageKey!, audience: input.audience!, elementId: input.elementId!, x: Math.min(1, Math.max(0, input.x)), y: Math.min(1, Math.max(0, input.y)), ...(input.range ? { range: { ...input.range } } : {}), ...(input.documentRevision ? { documentRevision: input.documentRevision } : {}) };
};
